import type { CompanySiteType } from '@api';
import { useUpdateCompanySite as useUpdateCompanySiteMutation } from '@api';
import { useNotification } from '@common';
import { AxiosError } from 'axios';
import { UpdateCompanySiteFormValuesType } from '../useUpdateCompanySiteFormSchema';

const TRANS_KEY = 'NOTIFICATIONS.UPDATE_COMPANY_SITE';

type UseUpdateCompanySiteReturnType = {
  updateCompanySite: (
    siteId: string,
    data: UpdateCompanySiteFormValuesType
  ) => Promise<void>;
};

export const useUpdateCompanySite = (
  companyId: string,
  site: CompanySiteType
): UseUpdateCompanySiteReturnType => {
  const { showSuccess, showError } = useNotification();
  const { mutateAsync: updateCompanySiteMutation } =
    useUpdateCompanySiteMutation();

  const onSuccess = async (): Promise<void> => {
    showSuccess({
      title: `${TRANS_KEY}.SUCCESS.TITLE`,
      message: `${TRANS_KEY}.SUCCESS.MESSAGE`,
    });
  };

  const onError = (error: AxiosError): void => {
    showError({
      title: `${TRANS_KEY}.ERROR.TITLE`,
      message: `${TRANS_KEY}.ERROR.MESSAGE`,
      error,
    });
  };

  const updateCompanySite = async (
    siteId: string,
    formValues: UpdateCompanySiteFormValuesType
  ): Promise<void> => {
    await updateCompanySiteMutation(
      {
        companyId,
        siteId,
        data: {
          address: {
            street: formValues.address.street || null,
            addition: formValues.address.addition || null,
            zip: formValues.address.zip || null,
            city: formValues.address.city || null,
            cityEnglish: site.address.cityEnglish ?? null,
            country: formValues.address.country || null,
            state: site.address.state ?? null,
          },
          contact: {
            phone: formValues.contact.phone || null,
            mobile: site.contact?.mobile ?? null,
            email: formValues.contact.email || null,
            fax: formValues.contact.fax || null,
          },
        },
      },
      { onSuccess, onError }
    );
  };

  return { updateCompanySite };
};
